import * as React from 'react';
import {useRef, useState} from 'react';
import {Button, Dropdown, Modal, Space, Tooltip} from 'antd';
import {CalendarOutlined, MoreOutlined} from '@ant-design/icons';

import {ServiceRequestModel, ServiceRequestStatus} from '@/typings/models/ServiceRequest';
import {LeadModel} from '@/typings/models/Lead';

import {F} from '@/utils/intl';
import {useDispatch, useIntl} from 'umi';
import PERMISSIONS from '@/constants/permissions';
import {useHasAccess, useServiceRequestPermissions} from '@/utils/hooks';

import styles from './index.less';
import DeclineServiceRequestAction
  from '@/components/Kanban/ServiceRequests/Calendar/Actions/DeclineServiceRequestAction';
import CancelServiceRequestAction
  from '@/components/Kanban/ServiceRequests/Calendar/Actions/CancelServiceRequestAction';
import RescheduleServiceRequestAction
  from '@/components/Kanban/ServiceRequests/Calendar/Actions/RescheduleServiceRequestAction';
import RemoveCalendarEventAction from '@/components/Kanban/ServiceRequests/Calendar/Actions/RemoveCalendarEventAction';
import ConfirmServiceRequestAction
  from '@/components/Kanban/ServiceRequests/Calendar/Actions/ConfirmServiceRequestAction';
import EditCalendarEventAction from '@/components/Kanban/ServiceRequests/Calendar/Actions/EditCalendarEventAction';
import {
  confirmServiceRequestRequest,
  declineServiceRequestRequest,
  deleteCalendarEventRequest,
} from '@/services/api/serviceRequest';
import {processAPIResponseNow} from '@/utils/request';
import {isRecurringEvent} from '@/utils/recurrence';
import {dispatchCalendarEventRemoval} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import {useRecurrenceScope} from '@/components/Kanban/ServiceRequests/Calendar/Recurrence/useRecurrenceScope';
import {RecurrenceScope} from '@/typings/models/Recurrence';
import {confirmCalendarEventDelete} from '@/components/Kanban/ServiceRequests/Calendar/EmailDecision';
import {usePubSub} from '@/context/PubSubContext';
import {APPOINTMENT_CHANGED} from '@/context/PubSubContext/events';

export interface ServiceRequestActionProps {
  serviceRequest: ServiceRequestModel;
  lead?: LeadModel;
  onActionEnd?: () => void;
  ghostActions?: boolean;
  hideActions?: boolean;
  size?: any;
  allActionsInDropdown?: boolean;
  cancelProps?: { open?: boolean; onOpenChange?: (open: boolean) => void };
  confirmProps?: { open?: boolean; onOpenChange?: (open: boolean) => void };
  declineProps?: { open?: boolean; onOpenChange?: (open: boolean) => void };
  rescheduleProps?: { onOpenReschedule?: () => void };
  editProps?: { onOpenEdit?: () => void };
  removeProps?: { open?: boolean; onOpenChange?: (open: boolean) => void };
  showActionText?: boolean;
  hideJumpAction?: boolean;
}

function addAction(
  allActionsInDropdown: boolean,
  actions: React.ReactNode[],
  menuItems: { key: string; label: React.ReactNode; onClick?: () => void; icon?: React.ReactNode; danger?: boolean }[],
  payload: {
    key: string;
    label: React.ReactNode;
    menuOnClick: () => void;
    component: React.ReactNode;
    icon?: React.ReactNode;
    danger?: boolean;
  },
) {
  const { key, label, menuOnClick, component, icon, danger } = payload;
  if (allActionsInDropdown) {
    menuItems.push({
      key,
      label,
      icon,
      danger,
      onClick: menuOnClick,
    });
    actions.push(<span key={`${key}-hidden`} style={{ display: 'none' }}>{component}</span>);
  } else {
    actions.push(component);
  }
}

function useServiceRequestActions({
  serviceRequest,
  lead,
  onActionEnd,
  ghostActions = false,
  hideActions = false,
  size,
  allActionsInDropdown = false,
  cancelProps,
  confirmProps,
  declineProps,
  rescheduleProps,
  editProps,
  removeProps,
  showActionText,
  hideJumpAction,
}: ServiceRequestActionProps) {
  const resolvedLead = lead || serviceRequest.lead;
  const intl = useIntl();
  const dispatch = useDispatch();
  const { publish } = usePubSub();
  const { askRecurrenceScope, recurrenceScopeDrawer } = useRecurrenceScope();
  const hasCreateForOthers = useHasAccess([PERMISSIONS.CalendarEvents.CREATE_FOR_OTHERS, PERMISSIONS.BUSINESS_PARTNER]);
  const { isOwner, hasServiceRequestActionAll, canPerformAction } = useServiceRequestPermissions(serviceRequest);

  const [cancelOpen, setCancelOpen] = useState(false);

  const rescheduleRef = useRef<HTMLElement>(null);
  const editRef = useRef<HTMLElement>(null);
  const jumpToCalendarRef = useRef<HTMLElement>(null);

  const isCustom = !serviceRequest.service || !serviceRequest.what;
  const isConfirmedOrTentative = [ServiceRequestStatus.confirmed, ServiceRequestStatus.tentative].includes(serviceRequest.status);
  const shouldShowJumpToAppointment = Boolean(serviceRequest?.branch_service?.branch?.id);

  const handleActionEnd = (Response) => {
    try {
      processAPIResponseNow(Response, {
        // Cancel / confirm / decline / calendar-event delete all land here. The
        // surface's own `onActionEnd` only refreshes itself, so the calendar and an
        // open lead drawer elsewhere would keep showing the stale appointment.
        onSuccess() {
          publish(APPOINTMENT_CHANGED, {
            appointmentId: serviceRequest.id,
            leadId: resolvedLead?.id,
          });
        },
      });
    } catch (error) {
    }
    onActionEnd?.(Response);

  }
  const handleConfirm = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'pages.serviceRequests.confirmAppointmentConfirm',
        defaultMessage: 'Are you sure to confirm this appointment?',
      }),
      onOk: () =>
        confirmServiceRequestRequest({
          params: { serviceRequestId: serviceRequest.id },
        }).then(handleActionEnd),
    });
  };

  const handleDecline = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'pages.serviceRequests.declineAppointmentConfirm',
        defaultMessage: 'Are you sure to decline this appointment?',
      }),
      onOk: () =>
        declineServiceRequestRequest({
          params: { serviceRequestId: serviceRequest.id },
        }).then(handleActionEnd),
    });
  };

  const handleCancel = () => setCancelOpen(true);

  const performCalendarEventDelete = (deleteScope?: RecurrenceScope, sendEmail?: boolean) =>
    deleteCalendarEventRequest({
      params: { calendarEventId: serviceRequest.id },
      // Nothing to notify without a linked customer: send no `send_email` at
      // all so the backend keeps its own notification default.
      ...(sendEmail === undefined ? {} : { data: { send_email: sendEmail } }),
      ...(deleteScope ? { query: { apply_to: deleteScope } } : {}),
    }).then((Response) => {
      // Reflect the removal in the store right away — the websocket echo does
      // the same for other clients and is idempotent with this.
      if (Response?.response?.ok)
        dispatchCalendarEventRemoval(dispatch, serviceRequest as any, deleteScope);
      return handleActionEnd(Response);
    });

  const handleRemove = async () => {
    // Calendar events are not gated by control_emails — anyone can pick. Only
    // lead-linked events notify a customer, so offer the choice just for those.
    // Defaults to off.
    const isLeadLinked = !!(serviceRequest.lead || serviceRequest.lead_id || serviceRequest.leadId || resolvedLead);
    const showEmailChoice = isLeadLinked;

    // Recurring custom events ask for scope (this / following / all) first.
    let scope: RecurrenceScope | undefined;
    if (isRecurringEvent(serviceRequest as any)) {
      scope = (await askRecurrenceScope({ action: 'delete' })) ?? undefined;
      if (!scope) return;
      // The scope drawer is already the confirmation step; only ask again when
      // there is a customer to notify.
      if (!showEmailChoice) return performCalendarEventDelete(scope);
    }

    // Delete confirmation popup with the notify-customer choice — same pattern
    // as the drag-and-drop reschedule confirm.
    const { confirmed, sendEmail } = await confirmCalendarEventDelete({
      title: intl.formatMessage({
        id: 'common.actions.removeAppointmentConfirm',
        defaultMessage: 'Are you sure to delete this appointment?',
      }),
      emailChoiceLabel: intl.formatMessage({
        id: 'pages.serviceRequest.cancelSendEmail',
        defaultMessage: 'Notify the customer by email',
      }),
      showEmailChoice,
    });
    if (!confirmed) return;
    return performCalendarEventDelete(scope, showEmailChoice ? sendEmail : undefined);
  };

  const jumpToAppointmentInCalendar = () => {
    location.replace(`/calendar?appointment_id=${serviceRequest.id}`);
  };

  if (hideActions) {
    return { menuItems: [], actions: [] };
  }

  const buttonProps = ghostActions
    ? { variant: 'text' as const, type: 'text' as const, color: 'default' as const, style: { opacity: 0.5 } }
    : {};

  const actions: React.ReactNode[] = [];
  const menuItems: { key: string; label: React.ReactNode; onClick?: () => void; icon?: React.ReactNode; danger?: boolean }[] = [];

  const push = (payload: {
    key: string;
    label: React.ReactNode;
    menuOnClick: () => void;
    component: React.ReactNode;
    icon?: React.ReactNode;
    danger?: boolean;
  }) => addAction(allActionsInDropdown, actions, menuItems, payload);


  const resolvedShowActionText = showActionText ?? allActionsInDropdown;

  if (!isCustom && isConfirmedOrTentative) {
    if (canPerformAction && serviceRequest.branch_service_id) {
      push({
        key: 'reschedule',
        label: <F id="pages.serviceRequest.reschedule" defaultMessage="Reschedule" />,
        menuOnClick: () => rescheduleRef.current?.click(),
        component: (
          <RescheduleServiceRequestAction
            key="reschedule"
            ref={rescheduleRef}
            lead={serviceRequest.lead || resolvedLead}
            onSuccess={handleActionEnd}
            serviceRequest={serviceRequest}
            buttonSize={size}
            showActionText={resolvedShowActionText}
            {...rescheduleProps}
            {...buttonProps}
          />
        ),
      });
    }

    if (canPerformAction && serviceRequest.status === ServiceRequestStatus.tentative) {
      push({
        key: 'confirm',
        label: <F id="pages.serviceRequests.confirm" defaultMessage="Confirm" />,
        menuOnClick: handleConfirm,
        component: (
          <ConfirmServiceRequestAction
            key="confirm"
            serviceRequest={serviceRequest}
            onSuccess={handleActionEnd}
            buttonSize={size}
            showActionText={resolvedShowActionText}
            {...confirmProps}
            {...buttonProps}
          />
        ),
      });
      push({
        key: 'decline',
        label: <F id="pages.serviceRequests.decline" defaultMessage="Decline" />,
        menuOnClick: handleDecline,
        component: (
          <DeclineServiceRequestAction
            key="decline"
            serviceRequest={serviceRequest}
            onSuccess={handleActionEnd}
            buttonSize={size}
            showActionText={resolvedShowActionText}
            {...declineProps}
            {...buttonProps}
          />
        ),
      });
    } else if (canPerformAction) {
      push({
        key: 'cancel',
        label: <F id="pages.serviceRequests.cancel" defaultMessage="Cancel" />,
        menuOnClick: handleCancel,
        component: (
          <CancelServiceRequestAction
            key="cancel"
            serviceRequest={serviceRequest}
            onSuccess={handleActionEnd}
            buttonSize={size}
            showActionText={resolvedShowActionText}
            // In dropdown mode the trigger is hidden, so a Popover can't anchor;
            // render the reason capture as a modal instead.
            modal={allActionsInDropdown}
            open={cancelProps?.open ?? cancelOpen}
            onOpenChange={(v) => {
              setCancelOpen(v);
              cancelProps?.onOpenChange?.(v);
            }}
            {...buttonProps}
          />
        ),
      });
    }

    if (!hideJumpAction && shouldShowJumpToAppointment) {
      const jumpLabel = <F id="pages.lead.jumpToDate" defaultMessage="Jump to this date in Calendar" />;
      push({
        key: 'jump',
        label: jumpLabel,
        icon: <CalendarOutlined />,
        menuOnClick: () => jumpToCalendarRef.current?.click(),
        component: (
          <Tooltip key="jump" title={jumpLabel}>
            <Button
              ref={jumpToCalendarRef}
              size={size || 'small'}
              shape="round"
              color="default"
              variant="filled"
              className={styles.icon}
              onClick={jumpToAppointmentInCalendar}
              icon={<CalendarOutlined />}
            />
          </Tooltip>
        ),
      });
    }
  } else if (isCustom) {
    const isExternalCalendarEvent = Boolean(serviceRequest.isExternal) || serviceRequest.sync_role === 'external';
    const canEditCalendarEvent = !isExternalCalendarEvent && (isOwner || hasCreateForOthers || hasServiceRequestActionAll);

    if (canEditCalendarEvent) {
      push({
        key: 'edit',
        label: <F id="common.actions.edit" defaultMessage="Edit" />,
        menuOnClick: () => editRef.current?.click(),
        component: (
          <EditCalendarEventAction
            key="edit"
            ref={editRef}
            calendarEvent={serviceRequest}
            onSuccess={handleActionEnd}
            lead={resolvedLead}
            buttonSize={size}
            showActionText={resolvedShowActionText}
            {...editProps}
          />
        ),
      });
      push({
        key: 'remove',
        label: <F id="common.actions.remove" defaultMessage="Delete" />,
        danger: true,
        menuOnClick: handleRemove,
        component: (
          <RemoveCalendarEventAction
            key="remove"
            {...buttonProps}
            {...removeProps}
            calendarEvent={serviceRequest}
            onSuccess={handleActionEnd}
            buttonSize={size}
            showActionText={resolvedShowActionText}
          />
        ),
      });
      // Scope prompt for the inline (dropdown menu) delete path of recurring events.
      actions.push(<React.Fragment key="recurrence-scope">{recurrenceScopeDrawer}</React.Fragment>);
    }
  }

  if (allActionsInDropdown && menuItems.length > 0) {
    actions.push(
      <Dropdown
        key="more-actions"
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={['click']}
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          size={size || 'small'}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>,
    );
  }

  return { menuItems, actions };
}

export function ServiceRequestAction(props: ServiceRequestActionProps) {
  const { actions } = useServiceRequestActions(props);

  if (actions.length === 0) {
    return null;
  }

  return (
    <Space onClick={(e) => e.stopPropagation()} size={3}>
      {actions}
    </Space>
  );
}

export default ServiceRequestAction;
